// Custom events framework (was: CREATIVE_EVENT_DEFS in game.ts).
//
// A custom event is a self-contained, deterministic GameEventDef plus the
// metadata the scheduler and HUD need:
//   rarity   — scheduling weight (common 100 / uncommon 45 / rare 15); the
//              EventsManager picks the next event by weighted roll.
//   danger   — 1..3 warning level for the HUD indicator (3 = deadly).
//   minWave  — director gate: the event is not schedulable before this wave.
//
// Adding a new event: implement createXEvent() in its own module here,
// append it to CUSTOM_EVENT_DEFS. No game.ts wiring required — the Game
// loops this registry once at construction.

import type { GameEventDef } from '../events-manager.js';
import { createMeteorShowerEvent } from './meteor-shower.js';
import { createIonStormEvent } from './ion-storm.js';
import { createGravityWellEvent } from './gravity-well.js';
import { createEventHorizonEvent } from './event-horizon.js';
import { createBloodMoonEvent } from './blood-moon.js';
import { createStarshowerEvent } from './starshower.js';
import { createChronalEddyEvent } from './chronal-eddy.js';
import { createSwarmSurgeEvent } from './swarm-surge.js';

// The rarity type and weight table live in events-manager.ts (the scheduler
// owns them); re-exported here so the registry stays the one import site.
export type { EventRarity } from '../events-manager.js';
export { EVENT_RARITY_WEIGHT } from '../events-manager.js';
import type { EventRarity } from '../events-manager.js';

export interface CustomEventDef extends GameEventDef {
  readonly rarity: EventRarity;
  readonly danger: 1 | 2 | 3;
  readonly minWave: number;
}

/**
 * Build a fresh set of custom-event defs. The singleton below is what the
 * Game registers; tests that run several worlds in one process must build
 * their own set — def instances keep per-activation closure state (seeded
 * streams, trackers) that is only valid for one world at a time.
 */
export function createCustomEventDefs(): CustomEventDef[] {
  return [
    Object.assign(createMeteorShowerEvent(), { rarity: 'uncommon', danger: 2, minWave: 2 } as const),
    Object.assign(createIonStormEvent(), { rarity: 'uncommon', danger: 1, minWave: 1 } as const),
    Object.assign(createGravityWellEvent(), { rarity: 'common', danger: 1, minWave: 1 } as const),
    Object.assign(createEventHorizonEvent(), { rarity: 'rare', danger: 3, minWave: 4 } as const),
    Object.assign(createBloodMoonEvent(), { rarity: 'rare', danger: 3, minWave: 3 } as const),
    Object.assign(createStarshowerEvent(), { rarity: 'uncommon', danger: 2, minWave: 2 } as const),
    Object.assign(createChronalEddyEvent(), { rarity: 'uncommon', danger: 1, minWave: 2 } as const),
    Object.assign(createSwarmSurgeEvent(), { rarity: 'common', danger: 2, minWave: 2 } as const),
  ];
}

export const CUSTOM_EVENT_DEFS: CustomEventDef[] = createCustomEventDefs();
